/**
 * Waltero Chart Theme Configuration
 * 
 * Provides branded color palettes and theme configurations for ECharts and Recharts
 * to maintain consistency with Waltero design system.
 */

// Import Waltero colors from main theme
import { walteroColors } from './walteroTheme';

// Primary Chart Color Palette - following Waltero brand guidelines
export const WALTERO_CHART_COLORS = [
  '#3d8dae', // Light Blue - primary data series
  '#23678b', // Medium Blue - secondary data series  
  '#f4994c', // Light Apricot - tertiary data series
  '#98c3cf', // Pale Blue - quaternary data series
  '#094872', // Core Blue - fifth data series
  '#f18849'  // Primary Apricot - sixth data series
];

// Extended color palette for complex charts with many series
export const WALTERO_EXTENDED_CHART_COLORS = [
  ...WALTERO_CHART_COLORS,
  '#c35d2c', // Dark Apricot
  '#d7e2e4', // Neutral Blue
  '#ffead3', // Pale Apricot
  '#023861', // Deep Blue
  '#edefef', // Background Blue
  '#fdf3eb'  // Background Apricot
];

// Semantic Status Colors for Charts
export const WALTERO_STATUS_COLORS = {
  success: '#f18849',    // Primary Apricot for positive/success metrics
  warning: '#f4994c',    // Light Apricot for warning states
  critical: '#c35d2c',   // Dark Apricot for critical/error states
  neutral: '#98c3cf',    // Pale Blue for neutral/inactive states
  info: '#3d8dae',       // Light Blue for informational data
  active: '#23678b',     // Medium Blue for active states
  inactive: '#d7e2e4'    // Neutral Blue for inactive states
};

// ECharts Theme Configuration
export const WALTERO_ECHARTS_THEME = {
  color: WALTERO_CHART_COLORS,
  backgroundColor: 'transparent',
  
  // Text styles following Waltero typography
  textStyle: {
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: 12,
    color: walteroColors.primary.coreBlue,
    fontWeight: 400
  },
  
  // Title configuration
  title: {
    textStyle: {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 16,
      fontWeight: 600,
      color: walteroColors.primary.coreBlue
    },
    subtextStyle: {
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 12,
      color: walteroColors.primary.mediumBlue
    }
  },
  
  // Line chart styles
  line: {
    itemStyle: {
      borderWidth: 2
    },
    lineStyle: {
      width: 2
    },
    symbolSize: 6,
    symbol: 'circle',
    smooth: true
  },
  
  // Bar chart styles
  bar: {
    itemStyle: {
      borderRadius: [4, 4, 0, 0],
      borderWidth: 0
    }
  },
  
  // Pie chart styles
  pie: {
    itemStyle: {
      borderRadius: 4,
      borderWidth: 2,
      borderColor: walteroColors.white
    }
  },
  
  // Grid configuration
  grid: {
    borderWidth: 0,
    backgroundColor: 'transparent',
    borderColor: walteroColors.primary.neutralBlue
  },
  
  // Axis configurations
  categoryAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: walteroColors.primary.neutralBlue,
        width: 1
      }
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: walteroColors.primary.neutralBlue
      }
    },
    axisLabel: {
      show: true,
      color: walteroColors.primary.mediumBlue,
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 11
    },
    splitLine: {
      show: false
    }
  },
  
  valueAxis: {
    axisLine: {
      show: true,
      lineStyle: {
        color: walteroColors.primary.neutralBlue,
        width: 1
      }
    },
    axisTick: {
      show: true,
      lineStyle: {
        color: walteroColors.primary.neutralBlue
      }
    },
    axisLabel: {
      show: true,
      color: walteroColors.primary.mediumBlue,
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 11
    },
    splitLine: {
      show: true,
      lineStyle: {
        color: walteroColors.primary.backgroundBlue,
        width: 1,
        type: 'solid'
      }
    }
  },
  
  // Legend configuration
  legend: {
    textStyle: {
      color: walteroColors.primary.coreBlue,
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 12
    },
    inactiveColor: walteroColors.primary.neutralBlue
  },
  
  // Tooltip configuration
  tooltip: {
    backgroundColor: walteroColors.white,
    borderColor: walteroColors.primary.neutralBlue,
    borderWidth: 1,
    borderRadius: 8,
    textStyle: {
      color: walteroColors.primary.coreBlue,
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: 12
    },
    extraCssText: `
      box-shadow: 0px 4px 12px rgba(2, 56, 97, 0.12);
      backdrop-filter: blur(8px);
    `
  },
  
  // DataZoom configuration
  dataZoom: {
    backgroundColor: walteroColors.primary.backgroundBlue,
    dataBackgroundColor: walteroColors.primary.paleBlue,
    fillerColor: `${walteroColors.secondary.primaryApricot}40`,
    handleColor: walteroColors.secondary.primaryApricot,
    handleSize: 16,
    textStyle: {
      color: walteroColors.primary.mediumBlue
    }
  }
};

// Recharts Theme Configuration
export const WALTERO_RECHARTS_THEME = {
  colors: WALTERO_CHART_COLORS,
  
  // Grid and axis styling
  grid: {
    stroke: walteroColors.primary.backgroundBlue,
    strokeWidth: 1,
    strokeDasharray: 'none'
  },
  
  // Axis styling
  axis: {
    stroke: walteroColors.primary.neutralBlue,
    strokeWidth: 1,
    tick: {
      fill: walteroColors.primary.mediumBlue,
      fontSize: 11,
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
    },
    tickLine: {
      stroke: walteroColors.primary.neutralBlue,
      strokeWidth: 1
    },
    axisLine: {
      stroke: walteroColors.primary.neutralBlue,
      strokeWidth: 1
    }
  },
  
  // Legend styling
  legend: {
    iconType: 'circle',
    fontSize: 12,
    color: walteroColors.primary.coreBlue,
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
  },
  
  // Tooltip styling
  tooltip: {
    backgroundColor: walteroColors.white,
    border: `1px solid ${walteroColors.primary.neutralBlue}`,
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(2, 56, 97, 0.12)',
    color: walteroColors.primary.coreBlue,
    fontSize: 12,
    fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif'
  }
};

// Default chart configuration generators
export const generateEChartsOption = (baseOption = {}) => {
  return {
    backgroundColor: 'transparent',
    color: WALTERO_CHART_COLORS,
    textStyle: WALTERO_ECHARTS_THEME.textStyle,
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '8%',
      containLabel: true,
      backgroundColor: 'transparent',
      ...WALTERO_ECHARTS_THEME.grid
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        animation: false,
        crossStyle: {
          color: walteroColors.primary.lightBlue
        }
      },
      ...WALTERO_ECHARTS_THEME.tooltip
    },
    legend: {
      ...WALTERO_ECHARTS_THEME.legend,
      top: 0,
      left: 'center'
    },
    ...baseOption
  };
};

// Performance optimization settings
export const PERFORMANCE_CONFIG = {
  // For large datasets (>1000 points)
  large: {
    animation: false,
    progressive: 1000,
    progressiveThreshold: 1000,
    large: true,
    largeThreshold: 1000,
    useUTC: true
  },
  
  // For medium datasets (100-1000 points)
  medium: {
    animation: true,
    animationDuration: 750,
    animationEasing: 'cubicOut',
    useUTC: true
  },
  
  // For small datasets (<100 points)
  small: {
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut',
    useUTC: true
  }
};

// Helper function to get performance config based on data size
export const getPerformanceConfig = (dataSize) => {
  if (dataSize > 1000) return PERFORMANCE_CONFIG.large;
  if (dataSize > 100) return PERFORMANCE_CONFIG.medium;
  return PERFORMANCE_CONFIG.small;
};

// Helper function to get color by index with fallback
export const getChartColor = (index, colors = WALTERO_CHART_COLORS) => {
  return colors[index % colors.length];
};

// Helper function to get status color
export const getStatusColor = (status) => {
  return WALTERO_STATUS_COLORS[status] || WALTERO_STATUS_COLORS.neutral;
};

export default {
  WALTERO_CHART_COLORS,
  WALTERO_EXTENDED_CHART_COLORS,
  WALTERO_STATUS_COLORS,
  WALTERO_ECHARTS_THEME,
  WALTERO_RECHARTS_THEME,
  generateEChartsOption,
  getPerformanceConfig,
  getChartColor,
  getStatusColor
};